import { IconButton, Tooltip, Popover } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo } from "react";
import { AiFillDelete, AiFillEye } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DisplayDate from "components/DisplayDate";
import { differenceInDays } from "date-fns";
import { getSubtaskProgress, getTaskAssignees } from "utils/taskUtils";
import TaskAssigneeAvatars from "components/Tasks/TaskAssigneeAvatars";
import StatusBadge from "components/Tasks/StatusBadge";
import PriorityBadge from "components/Tasks/PriorityBadge";

const TableView = ({
  tasksData,
  isLoading,
  isFetching,
  anchorEl,
  selectedRow,
  open,
  id,
  handleClick,
  handleClose,
  setOpenConfirmationModal,
}) => {
  const navigate = useNavigate();
  const isDeadlinePassed = (taskDetails) => {
    const daysUntilDeadline = differenceInDays(new Date(taskDetails?.deadline), new Date());
    if (taskDetails?.status === "COMPLETED" || taskDetails?.status === "CANCELLED") {
      return false;
    } else {
      return { days: daysUntilDeadline + 1, isPassed: daysUntilDeadline < 0 };
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "specificTask",
        header: "Title",
        size: 180,
        Cell: ({ renderedCellValue }) => (
          <span className="font-medium text-app-text">{renderedCellValue || "Untitled task"}</span>
        ),
      },

      {
        accessorKey: "clientName",
        header: "Client Name",
        size: 120,
        Cell: ({ renderedCellValue }) => (
          <Tooltip title={renderedCellValue}>
            <div className="flex items-center truncate max-w-[200px]">
              <div className="mr-2 truncate">{renderedCellValue}</div>
            </div>
          </Tooltip>
        ),
      },
      {
        accessorKey: "module",
        header: "Module",
        size: 100,
      },
      {
        accessorKey: "assignedBy.name",
        header: "Assigned By",
        size: 150,
        Cell: ({ renderedCellValue }) => {
          return (
            <Tooltip title={renderedCellValue}>
              <div className="flex items-center">{renderedCellValue}</div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "assignedTo.name",
        header: "Assigned To",
        size: 170,
        Cell: ({ row }) => {
          const assignees = getTaskAssignees(row.original);
          return (
            <Tooltip title={assignees.map((a) => a.name).join(", ")}>
              <div className="flex items-center gap-2">
                <span className="truncate">
                  {assignees[0]?.name || "-"}
                  {assignees.length > 1 && ` +${assignees.length - 1}`}
                </span>
                {assignees.length > 1 && (
                  <TaskAssigneeAvatars people={assignees} max={3} size={20} />
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        id: "subtaskProgress",
        header: "Subtasks",
        size: 100,
        accessorFn: (row) => getSubtaskProgress(row).total,
        Cell: ({ row }) => {
          const progress = getSubtaskProgress(row.original);
          if (!progress.total) return <span className="text-app-text-muted">-</span>;
          return (
            <span className="text-sm text-app-text">
              {progress.done}/{progress.total}
            </span>
          );
        },
      },
      {
        accessorKey: "priority",
        header: "Priority",
        size: 100,
        Cell: ({ renderedCellValue }) => <PriorityBadge priority={renderedCellValue} />,
      },
      {
        id: "createdDate",
        accessorFn: (row) => row.createdDate ?? row.createdAt,
        header: "Created Date",
        size: 150,
        Cell: ({ renderedCellValue }) => <DisplayDate value={renderedCellValue} />,
      },
      {
        accessorKey: "deadline",
        header: "Deadline",
        size: 150,
        Cell: ({ row }) => {
          return (
            <div
              className="flex flex-col items-start max-w-[150px] truncate"
              style={
                isDeadlinePassed(row?.original)?.isPassed
                  ? { color: "var(--app-priority-high-text)" }
                  : undefined
              }
            >
              <DisplayDate value={row?.original?.deadline} />
              {isDeadlinePassed(row?.original)?.days > 0 && (
                <span className="text-xs text-app-text-secondary">
                  {isDeadlinePassed(row?.original)?.days}
                  day{isDeadlinePassed(row?.original)?.days > 1 ? "s" : ""} left
                </span>
              )}
              {isDeadlinePassed(row?.original)?.days <= 0 && (
                <span className="text-xs" style={{ color: "var(--app-priority-high-text)" }}>
                  Deadline already passed.
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        Cell: ({ renderedCellValue }) => <StatusBadge status={renderedCellValue} />,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="flex items-center">
              <IconButton onClick={(e) => handleClick(e, item)}>
                <MoreVertIcon className="text-app-text-secondary" />
              </IconButton>
              <Popover
                id={id}
                open={open && selectedRow?.id === item.id}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <div className="p-2 min-w-[200px]">
                  <div
                    className="flex items-center gap-2 p-2 hover:bg-app-surface-hover rounded cursor-pointer"
                    onClick={() => {
                      navigate(`/admin/tasks/view/${item?.id}`, {
                        state: { item },
                      });
                      handleClose();
                    }}
                  >
                    <AiFillEye className="text-primary" />
                    <span>View Task</span>
                  </div>

                  {/* Deleting a whole task belongs to its assigner and org super admins, the same
                      right the server reports as canEditDetails. */}
                  {item?.canEditDetails && (
                    <div
                      className="flex items-center gap-2 p-2 hover:bg-app-surface-hover rounded cursor-pointer text-red-600"
                      onClick={() => {
                        setOpenConfirmationModal({
                          state: true,
                          id: item?.id,
                        });
                        handleClose();
                      }}
                    >
                      <AiFillDelete className="text-red-600" />
                      <span>Delete Task</span>
                    </div>
                  )}
                </div>
              </Popover>
            </div>
          );
        },
      },
    ],
    [open, anchorEl, selectedRow, handleClick, handleClose, setOpenConfirmationModal]
  );

  const table = useMaterialReactTable({
    columns,
    data: tasksData || [],
    rowNumberDisplayMode: "original",
    enableRowNumbers: true,
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
    },
    enablePagination: false,
    enableStickyHeader: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest(".MuiPopover-root");
        if (!isActionClick) {
          navigate(`/admin/tasks/view/${row.original.id}`, {
            state: { item: row.original },
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "var(--app-table-row-hover)",
        },
      },
    }),
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        minHeight: "70vh",
        overflowY: "auto",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "var(--app-table-header)",
        color: "var(--app-text-secondary)",
        fontWeight: 700,
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        borderBottom: "1px solid var(--app-border)",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: "var(--app-text)",
        borderBottom: "1px solid var(--app-border-subtle)",
      },
    },
    muiTablePaperProps: {
      sx: {
        backgroundColor: "var(--app-surface)",
        backgroundImage: "none",
        boxShadow: "none",
      },
    },
    state: {
      isLoading: isLoading || isFetching,
      showProgressBars: isLoading || isFetching,
      isSaving: isLoading || isFetching,
    },
  });

  return (
    <div className="block w-full overflow-x-auto">
      <MaterialReactTable table={table} />
    </div>
  );
};

export default TableView;
